import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import Button from '@/src/components/common/Elements/Button/Button';
import Icon, { ICONS } from '@/src/components/common/Elements/Icon/Icon';
import InlineImage from '@/src/components/common/Elements/Image/InlineImage';
import SectionLoader from '@/src/components/common/Elements/Spinner/SectionLoader/SectionLoader';
import type { FileEntry } from '@/src/components/project/Inbox2/types';
import { useCompleteListPagination } from '@/src/hooks/useListCompletePagination';
import { INTERNAL_USER_ROLES, checkAccess } from '@/src/lib/authorization/authorization';
import { RootState } from '@/src/reducers/types';
import { parseNumberToStringWithSeparator } from '@/src/util/numbers';
import { cn } from '@/src/util/ui';

import { useGetDrafts } from '../../api/getDrafts';
import { useTreatmentOptions } from '../../api/getRemoteOptions';
import { useQuickbookTransactions } from '../../api/getTreatments';
import { DEFAULT_CURRENCY, DEFAULT_EMPTY_VALUE } from '../../Config';
import ActionHeader from '../../Elements/ActionHeader/ActionHeader';
import Table from '../../Elements/Table/Table';
import { mapTreatments, mapDrafts } from '../../Helper';
import { TreatmentsStore } from '../../store/treatments.store';
import { Draft } from '../../types/draft';
import { Treatment } from '../../types/treatment';
import AutomationBanner from '../Automation/AutomationBanner';
import { BaseFormOption } from '../TreatmentForm/BaseForm/BaseForm';
import { CheckPaymentFormOption } from '../TreatmentForm/CheckPaymentForm/CheckPaymentForm';
import { PaidBillFormOption } from '../TreatmentForm/PaidBillForm/PaidBillForm';
import { UnpaidBillFormOption } from '../TreatmentForm/UnpaidBillForm/UnpaidBillForm';

const TREATMENTS_LIMIT = 10;

const sortTreatmentAndDraft = (
  unsortedList: Array<Draft | Treatment>
): Array<Draft | Treatment> => {
  const draftWithoutDate = [];
  const treatmentAndDraftList = unsortedList.filter((item) => {
    if (!item.transactionDate) {
      draftWithoutDate.push(item);
      return false;
    }
    return true;
  });

  const treatmentSortedByDate = treatmentAndDraftList.sort((a, b) => {
    if (a.transactionDate < b.transactionDate) {
      return 1;
    }
    if (a.transactionDate > b.transactionDate) {
      return -1;
    }
    return 0;
  });

  return [...draftWithoutDate, ...treatmentSortedByDate];
};

type TreatmentListProps = {
  setTreatment: (treatment: TreatmentsStore['treatment']) => void;
  setDraft: (draft: TreatmentsStore['draft']) => void;
  setForm: (form: TreatmentsStore['form']) => void;
  onCloseFailedAutomationBanner: () => void;
  onClickAutomate: () => void;
  isViewable: boolean;
  fileEntry: FileEntry;
  isFailedAutomation: boolean;
};

const TreatmentList = ({
  setTreatment,
  setDraft,
  setForm,
  fileEntry,
  isViewable,
  isFailedAutomation,
  onCloseFailedAutomationBanner,
  onClickAutomate,
}: TreatmentListProps) => {
  const documentId = fileEntry.file.id;
  const inboxDocumentId = fileEntry.id;
  const loggedUser = useSelector((store: RootState) => store.loggedUser);
  const isInternalUser = checkAccess({ user: loggedUser.data, allowedRoles: INTERNAL_USER_ROLES });
  const FORM_TYPES = useMemo(() => {
    return {
      Bill: UnpaidBillFormOption,
      Expense: PaidBillFormOption,
      Check: CheckPaymentFormOption,
      Base: BaseFormOption,
    };
  }, []);
  const [publishedTreatmentCount, setPublishedTreatmentCount] = useState<number>(0);
  const [treatmentList, setTreatmentList] = useState<Array<Treatment | Draft>>([]);
  const treatmentsQuery = useQuickbookTransactions({
    inboxDocumentId,
  });

  const draftsQuery = useGetDrafts({
    documentId,
    config: {
      enabled: isInternalUser,
    },
  });

  const isRemoteOptionQueryEnabled = !!draftsQuery.data && draftsQuery?.data.length > 0;

  const remoteOptionsQuery = useTreatmentOptions({
    fileId: documentId,
    config: {
      enabled: isRemoteOptionQueryEnabled,
    },
  });

  const isLoading =
    treatmentsQuery.isLoading ||
    (draftsQuery.isLoading && draftsQuery.isFetching) ||
    (remoteOptionsQuery.isLoading && remoteOptionsQuery.isFetching);
  const isFetching =
    treatmentsQuery.isFetching || draftsQuery.isFetching || remoteOptionsQuery.isFetching;

  const {
    paginatedList,
    hasMore,
    loadMore,
    isLoading: isPaginating,
  } = useCompleteListPagination({
    list: treatmentList,
    maxPerPage: TREATMENTS_LIMIT,
  });

  useEffect(() => {
    if (
      !treatmentsQuery.data ||
      (isInternalUser && !draftsQuery.data) ||
      (isRemoteOptionQueryEnabled && !remoteOptionsQuery.data)
    ) {
      return;
    }

    const treatments = mapTreatments(treatmentsQuery.data.transactions) as Treatment[];
    const drafts = draftsQuery.data
      ? (mapDrafts(draftsQuery.data, remoteOptionsQuery.data) as Draft[])
      : [];
    const sortedList = sortTreatmentAndDraft([...drafts, ...treatments]);

    setTreatmentList(sortedList);
    setPublishedTreatmentCount(treatments.length);
  }, [
    treatmentsQuery.data,
    draftsQuery.data,
    remoteOptionsQuery.data,
    isInternalUser,
    isRemoteOptionQueryEnabled,
  ]);

  const handleClickTreatmentAndDraft = useCallback(
    (item: Treatment | Draft) => {
      if ((item as Draft).draftId) {
        setDraft(item as Draft);
        setForm(item.type ? FORM_TYPES[item.type] : FORM_TYPES.Base);
        return;
      }

      if (isViewable) {
        setTreatment(item as Treatment);
      }
    },
    [isViewable, setDraft, setForm, FORM_TYPES, setTreatment]
  );

  const Banner = useMemo(
    () =>
      isFailedAutomation && (
        <AutomationBanner onClose={onCloseFailedAutomationBanner}>
          <Icon
            icon={ICONS.automation_off({
              // strokeClasses: 'tw-stroke-cfo-purple-200 tw-fill-cfo-purple-200',
              width: '10px',
              height: '16px',
              fill: '#9747FF',
            })}
            className={clsx('tw-text-[15px] tw-mr-[5px]')}
          />
          We’re unable to generate draft this time. You can still try again.
        </AutomationBanner>
      ),
    [isFailedAutomation, onCloseFailedAutomationBanner]
  );

  return (
    <React.Fragment>
      <ActionHeader
        fileEntry={fileEntry}
        hasPublishedTreatment={publishedTreatmentCount > 0}
        onClickAutomate={onClickAutomate}
      />
      {Banner}
      <div
        className={cn(
          'treatmentList--container',
          'tw-overflow-y-auto tw-scroll-styles',
          'tw-h-[calc(100%-63px)]'
        )}
      >
        <Table
          data={paginatedList}
          onClickRow={handleClickTreatmentAndDraft}
          containerClassName={clsx('!tw-border-0 !tw-border-t-[1px]')}
          rowClassName={clsx(
            'tw-cursor-pointer hover:tw-bg-cfo-blue-200 active:tw-bg-cfo-blue-200'
          )}
          columns={[
            {
              title: '',
              width: '10%',
              Cell({ entry }) {
                if (!entry.draftId) {
                  return (
                    <InlineImage
                      src={`url(/images/quickbooks-icon1.png)`}
                      widthClasses={clsx('tw-w-[15px]')}
                      heightClasses={clsx('tw-min-h-[15px]')}
                    />
                  );
                }
                return entry.draftId && entry.automationId && !entry.modifiedBy ? (
                  <Icon
                    icon={ICONS.automation_on({
                      strokeClasses: 'tw-stroke-cfo-purple-200 tw-fill-cfo-purple-200',
                      width: '16px',
                      height: '16px',
                    })}
                    className={clsx('tw-text-[15px] tw-mr-[5px]')}
                  />
                ) : (
                  <Icon icon={ICONS.draft_icon({ className: '' })} />
                );
              },
            },
            {
              title: 'Type',
              width: '25%',
              Cell({ entry }) {
                const typeDisplayName = entry.typeDisplayName;
                return typeDisplayName ? (
                  <span>{typeDisplayName}</span>
                ) : (
                  <span className={clsx('tw-text-cfo-grey-600')}>[Type]</span>
                );
              },
            },
            {
              title: 'Document Ref',
              width: '35%',
              Cell({ entry }) {
                const referenceNumber = entry.payload.referenceNumber;
                if (entry.draftId) {
                  return referenceNumber ? (
                    <span>{referenceNumber}</span>
                  ) : (
                    <span className={clsx('tw-text-cfo-grey-600')}>[Document Ref]</span>
                  );
                }
                return <span> {referenceNumber || DEFAULT_EMPTY_VALUE}</span>;
              },
            },
            {
              title: 'Amount',
              width: '50%',
              textAlignment: 'tw-text-right',
              Cell({ entry }) {
                const totalAmount = entry.payload.totalAmount;
                const currency = entry.payload?.currency?.id || DEFAULT_CURRENCY.id;
                const amount = `${currency} ${parseNumberToStringWithSeparator(totalAmount, 2)}`;
                if (entry.draftId) {
                  return totalAmount > 0.0 ? (
                    <span>{amount}</span>
                  ) : (
                    <span className={clsx('tw-text-cfo-grey-600')}>[Amount]</span>
                  );
                }
                return <span>{amount}</span>;
              },
            },
          ]}
          isLoading={isLoading || isFetching}
        />

        {treatmentList.length < 1 && !isLoading && (
          <div
            className={clsx(
              'tw-flex tw-items-center tw-justify-center',
              'tw-text-center',
              'tw-min-h-[calc(100vh-340px)]'
            )}
          >
            <h2
              className={clsx(
                // (!) is not removed here as it is to override the styles from vendor.css
                'tw-font-brandonTextMedium !tw-text-[14px] tw-text-cfo-grey-600',
                'tw-m-0'
              )}
            >
              No available data
            </h2>
          </div>
        )}

        {isPaginating && <SectionLoader />}
        {hasMore && !isPaginating && (
          <div className={clsx('tw-w-full', 'tw-text-right', 'tw-py-[15px] tw-px-[20px] ')}>
            <Button
              className={clsx(
                'tw-font-brandonTextMedium tw-text-cfo-blue-600',
                'tw-mr-0',
                'hover:tw-underline'
              )}
              onClick={loadMore}
            >
              Load More
            </Button>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default TreatmentList;
